import { CallClient, Features } from "@azure/communication-calling";

// Troubleshooting helpers for the Preview Screen: collect device/browser details, test the
// camera, microphone and speaker, and build a plain-text log the customer can paste into a
// support ticket. Everything here is read-only except the short-lived test streams, which
// are always stopped before returning.

const MAX_CONSOLE_ENTRIES = 200;
const MAX_ENTRY_LENGTH = 500;
const consoleEntries = [];
let isConsoleCaptureInstalled = false;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => {
        const error = new Error(message);
        error.name = "TimeoutError";
        reject(error);
      }, ms)
    ),
  ]);

// ACS tokens and other bearer values are JWTs - never let one leak into a shared log
const redact = (text) => text.replace(/eyJ[\w-]{10,}\.[\w-]{10,}\.[\w-]*/g, "[redacted-token]");

const stringifyArg = (arg) => {
  try {
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    if (typeof arg === "object" && arg !== null) {
      if (arg.name && arg.message) return `${arg.name}: ${arg.message}`;
      return JSON.stringify(arg);
    }
    return String(arg);
  } catch (e) {
    return "[unserializable]";
  }
};

const pushConsoleEntry = (level, args) => {
  try {
    let text = redact(args.map(stringifyArg).join(" "));
    if (text.length > MAX_ENTRY_LENGTH) text = text.slice(0, MAX_ENTRY_LENGTH) + "…";
    consoleEntries.push(`${new Date().toISOString().slice(11, 23)} [${level}] ${text}`);
    if (consoleEntries.length > MAX_CONSOLE_ENTRIES) consoleEntries.shift();
  } catch (e) {
  }
};

// Called once from index.js so the log also contains what happened before the user opened
// the troubleshooter (ACS device errors, permission failures, etc.)
export const installConsoleCapture = () => {
  if (isConsoleCaptureInstalled) return;
  isConsoleCaptureInstalled = true;
  ["log", "info", "warn", "error", "debug"].forEach((level) => {
    const original = console[level];
    if (typeof original !== "function") return;
    console[level] = (...args) => {
      pushConsoleEntry(level, args);
      original.apply(console, args);
    };
  });
  window.addEventListener("error", (event) => {
    pushConsoleEntry("uncaught", [event.message || event.error]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    pushConsoleEntry("unhandled-rejection", [event.reason]);
  });
};

export const getConsoleEntries = () => [...consoleEntries];

const parseBrowser = (userAgent) => {
  const rules = [
    ["Edge", /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ["Opera", /OPR\/([\d.]+)/],
    ["Firefox", /(?:Firefox|FxiOS)\/([\d.]+)/],
    ["Chrome", /(?:Chrome|CriOS)\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
  ];
  for (const [name, regex] of rules) {
    const match = userAgent.match(regex);
    if (match) return { name, version: match[1] };
  }
  return { name: "Unknown", version: "Unknown" };
};

const parseOs = (userAgent, highEntropy) => {
  const isIPadOs = /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(userAgent) || isIPadOs) {
    const match = userAgent.match(/OS (\d+[_.]\d+(?:[_.]\d+)?)/);
    return { name: isIPadOs ? "iPadOS" : "iOS", version: match ? match[1].replace(/_/g, ".") : "Unknown" };
  }
  if (/Android/.test(userAgent)) {
    const match = userAgent.match(/Android ([\d.]+)/);
    return { name: "Android", version: match ? match[1] : "Unknown" };
  }
  if (/Macintosh|Mac OS X/.test(userAgent)) {
    // Browsers freeze the macOS version in the user agent at 10.15.7 - only Chromium's
    // client hints expose the real version (e.g. 26.x for Tahoe)
    if (highEntropy?.platformVersion) return { name: "macOS", version: highEntropy.platformVersion };
    const match = userAgent.match(/Mac OS X (\d+[_.]\d+(?:[_.]\d+)?)/);
    const uaVersion = match ? match[1].replace(/_/g, ".") : "Unknown";
    return { name: "macOS", version: `${uaVersion} (frozen by browser - real version hidden)` };
  }
  if (/Windows/.test(userAgent)) {
    // Client hints: platformVersion >= 13 means Windows 11
    if (highEntropy?.platformVersion) {
      const major = parseInt(highEntropy.platformVersion, 10);
      return { name: "Windows", version: major >= 13 ? "11" : "10 or older" };
    }
    return { name: "Windows", version: "Unknown" };
  }
  if (/CrOS/.test(userAgent)) return { name: "ChromeOS", version: "Unknown" };
  if (/Linux/.test(userAgent)) return { name: "Linux", version: "Unknown" };
  return { name: "Unknown", version: "Unknown" };
};

const getGpuRenderer = () => {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "WebGL unavailable";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    return ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
  } catch (e) {
    return "Unknown";
  }
};

const allowsFeature = (feature) => {
  try {
    const policy = document.permissionsPolicy || document.featurePolicy;
    if (!policy?.allowsFeature) return "Not reported";
    return policy.allowsFeature(feature) ? "Allowed" : "BLOCKED";
  } catch (e) {
    return "Not reported";
  }
};

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const collectSystemInfo = async () => {
  const userAgent = navigator.userAgent;
  let highEntropy = null;
  try {
    if (navigator.userAgentData?.getHighEntropyValues) {
      highEntropy = await withTimeout(
        navigator.userAgentData.getHighEntropyValues([
          "platform",
          "platformVersion",
          "architecture",
          "bitness",
          "model",
          "fullVersionList",
        ]),
        2000,
        "Client hints timed out"
      );
    }
  } catch (e) {
  }

  const browser = parseBrowser(userAgent);
  const fullVersion = highEntropy?.fullVersionList?.find((item) =>
    /Chrome|Edge|Opera/i.test(item.brand) && !/Chromium/i.test(item.brand)
  )?.version;
  const os = parseOs(userAgent, highEntropy);
  const connection = navigator.connection || {};

  // Browsers never expose the exact hardware model (e.g. "MacBook Air M2") for privacy;
  // CPU architecture + GPU name is the closest we can get
  const architecture = highEntropy?.architecture
    ? `${highEntropy.architecture}${highEntropy.bitness ? ` ${highEntropy.bitness}-bit` : ""}`
    : "Not reported";
  const gpu = getGpuRenderer();
  const appleSiliconHint = /Apple M\d|Apple GPU/i.test(gpu) || highEntropy?.architecture === "arm";

  return {
    "Device model": highEntropy?.model || (os.name === "macOS" ? (appleSiliconHint ? "Mac (Apple Silicon)" : "Mac (Intel or unknown)") : "Not reported by browser"),
    "Operating system": `${os.name} ${os.version}`,
    Browser: `${browser.name} ${fullVersion || browser.version}`,
    "CPU architecture": architecture,
    "CPU cores": navigator.hardwareConcurrency || "Not reported",
    "Device memory": navigator.deviceMemory ? `${navigator.deviceMemory} GB` : "Not reported",
    GPU: gpu,
    "Touch points": navigator.maxTouchPoints || 0,
    Screen: `${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`,
    Viewport: `${window.innerWidth}x${window.innerHeight}`,
    Language: navigator.language,
    "Time zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    Online: navigator.onLine ? "Yes" : "No",
    Network: connection.effectiveType
      ? `${connection.effectiveType}, ~${connection.downlink} Mbps, RTT ${connection.rtt} ms`
      : "Not reported",
    "Secure context (HTTPS)": window.isSecureContext ? "Yes" : "NO",
    "Embedded in iframe": isInIframe() ? "Yes" : "No",
    "Page policy - camera": allowsFeature("camera"),
    "Page policy - microphone": allowsFeature("microphone"),
    "Page policy - speaker-selection": allowsFeature("speaker-selection"),
    "getUserMedia available": navigator.mediaDevices?.getUserMedia ? "Yes" : "NO",
    "Speaker selection (setSinkId)": typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype ? "Supported" : "Not supported",
    "Permissions API": navigator.permissions?.query ? "Available" : "Unavailable",
    "User agent": userAgent,
  };
};

export const collectAcsEnvironment = async () => {
  try {
    const callClient = new CallClient();
    const debugInfo = callClient.feature(Features.DebugInfo);
    const info = await withTimeout(debugInfo.getEnvironmentInfo(), 5000, "ACS environment check timed out");
    return {
      "Supported environment": info.isSupportedEnvironment ? "Yes" : "NO",
      "Supported platform": info.isSupportedPlatform ? "Yes" : "NO",
      "Supported browser": info.isSupportedBrowser ? "Yes" : "NO",
      "Supported browser version": info.isSupportedBrowserVersion ? "Yes" : "NO",
      "Detected as": `${info.environment?.platform || "?"} / ${info.environment?.browser || "?"} ${info.environment?.browserVersion || ""}`.trim(),
      "Video call open in another tab": debugInfo.isCallClientActiveInAnotherTab ? "YES" : "No",
    };
  } catch (e) {
    return { Error: `${e.name}: ${e.message}` };
  }
};

export const queryPermission = async (name) => {
  if (!navigator.permissions?.query) return "unavailable";
  try {
    return (await navigator.permissions.query({ name })).state;
  } catch (e) {
    return "unsupported";
  }
};

export const listDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.map((device) => ({
      kind: device.kind,
      label: device.label || "(name hidden until permission is granted)",
      deviceId: device.deviceId,
    }));
  } catch (e) {
    return [{ kind: "error", label: `${e.name}: ${e.message}`, deviceId: "" }];
  }
};

const shortId = (id) => (id ? id.slice(0, 8) : "none");

// Plain-language explanation + fix for each getUserMedia failure, with the macOS-specific
// causes we see most (OS-level privacy block, camera held by another app)
export const explainMediaError = (error, kind) => {
  const device = kind === "camera" ? "camera" : "microphone";
  const message = `${error?.message || ""}`;
  switch (error?.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      if (/by system/i.test(message)) {
        return `macOS is blocking the browser from using the ${device}. Open System Settings > Privacy & Security > ${kind === "camera" ? "Camera" : "Microphone"}, turn the switch ON for this browser, then fully quit (Cmd+Q) and reopen the browser.`;
      }
      return `The ${device} permission was denied. Allow the ${device} for this site in the browser's site settings, and on a Mac also check System Settings > Privacy & Security > ${kind === "camera" ? "Camera" : "Microphone"}.`;
    case "NotReadableError":
    case "TrackStartError":
      return `The ${device} exists but could not be started - it is usually in use by another app (Zoom, Teams, FaceTime, Photo Booth, another browser tab). Close those apps and try again. If it still fails, restart the computer.`;
    case "NotFoundError":
    case "DevicesNotFoundError":
      return `No ${device} was found. Check that it is connected and enabled.`;
    case "OverconstrainedError":
      return `The selected ${device} is no longer available (it may have been unplugged or renamed). Select a different ${device} in the device list.`;
    case "SecurityError":
      return `The browser blocked ${device} access for security reasons (page not on HTTPS or blocked by a policy).`;
    case "AbortError":
      return `The ${device} start was interrupted by the operating system. Close other apps using it and try again.`;
    case "TimeoutError":
      return `The ${device} did not respond. A permission prompt may be waiting for an answer, or the device is stuck - try restarting the browser.`;
    default:
      return `Unexpected ${device} error: ${error?.name || "Unknown"} ${message}`.trim();
  }
};

const getMediaWithFallback = async (kind, deviceId) => {
  const exact = deviceId ? { deviceId: { exact: deviceId } } : true;
  try {
    return { stream: await withTimeout(navigator.mediaDevices.getUserMedia({ [kind]: exact }), 15000, `${kind} did not start within 15s`), usedFallback: false };
  } catch (error) {
    // A stale stored deviceId (Mac Safari rotates them) should not hide a working default device
    if (deviceId && (error.name === "OverconstrainedError" || error.name === "NotFoundError")) {
      return { stream: await withTimeout(navigator.mediaDevices.getUserMedia({ [kind]: true }), 15000, `${kind} did not start within 15s`), usedFallback: true };
    }
    throw error;
  }
};

const describeTrack = (track) => {
  const settings = track.getSettings ? track.getSettings() : {};
  return {
    label: track.label || "(no name)",
    readyState: track.readyState,
    enabled: track.enabled,
    muted: track.muted,
    settings,
  };
};

// Renders the stream into an off-screen <video> and samples a few frames so we can tell a
// working camera from one that delivers black or frozen frames (a common Mac symptom when
// another app or the OS holds the camera while the track still reports "live")
const analyzeVideoFrames = async (stream) => {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  const started = performance.now();
  const firstFrame = new Promise((resolve, reject) => {
    video.onloadeddata = resolve;
    video.onerror = () => reject(new Error("Video element failed to load the stream"));
  });
  video.srcObject = stream;
  try {
    await video.play();
  } catch (e) {
  }
  try {
    if (video.readyState < 2) await withTimeout(firstFrame, 6000, "No video frames received within 6s");
  } catch (error) {
    video.srcObject = null;
    return { framesReceived: false, error: error.message };
  }
  const firstFrameMs = Math.round(performance.now() - started);

  const width = 64;
  const height = 36;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const samples = [];
  let previous = null;
  const diffs = [];
  for (let i = 0; i < 5; i++) {
    await delay(250);
    ctx.drawImage(video, 0, 0, width, height);
    const data = ctx.getImageData(0, 0, width, height).data;
    const luma = new Float32Array(width * height);
    let sum = 0;
    for (let p = 0, j = 0; p < data.length; p += 4, j++) {
      luma[j] = 0.2126 * data[p] + 0.7152 * data[p + 1] + 0.0722 * data[p + 2];
      sum += luma[j];
    }
    const mean = sum / luma.length;
    let variance = 0;
    for (let j = 0; j < luma.length; j++) variance += (luma[j] - mean) ** 2;
    samples.push({ mean, stdDev: Math.sqrt(variance / luma.length) });
    if (previous) {
      let diff = 0;
      for (let j = 0; j < luma.length; j++) diff += Math.abs(luma[j] - previous[j]);
      diffs.push(diff / luma.length);
    }
    previous = luma;
  }

  const snapshotCanvas = document.createElement("canvas");
  snapshotCanvas.width = 320;
  snapshotCanvas.height = Math.round(320 * ((video.videoHeight || 9) / (video.videoWidth || 16)));
  snapshotCanvas.getContext("2d").drawImage(video, 0, 0, snapshotCanvas.width, snapshotCanvas.height);
  let snapshot = null;
  try {
    snapshot = snapshotCanvas.toDataURL("image/jpeg", 0.7);
  } catch (e) {
  }
  video.pause();
  video.srcObject = null;

  const avgBrightness = samples.reduce((acc, s) => acc + s.mean, 0) / samples.length;
  const avgDetail = samples.reduce((acc, s) => acc + s.stdDev, 0) / samples.length;
  const avgMotion = diffs.reduce((acc, d) => acc + d, 0) / (diffs.length || 1);
  return {
    framesReceived: true,
    firstFrameMs,
    resolution: `${video.videoWidth}x${video.videoHeight}`,
    avgBrightness: Math.round(avgBrightness),
    avgDetail: Math.round(avgDetail * 10) / 10,
    avgFrameChange: Math.round(avgMotion * 100) / 100,
    isBlack: avgBrightness < 10 && avgDetail < 4,
    isFrozen: diffs.length > 0 && diffs.every((d) => d === 0),
    snapshot,
  };
};

const getAppStream = async (previewLocalVideoStream) => {
  try {
    if (!previewLocalVideoStream?.getMediaStream) return null;
    const stream = await withTimeout(previewLocalVideoStream.getMediaStream(), 3000, "timeout");
    return stream?.getVideoTracks().length ? stream : null;
  } catch (e) {
    return null;
  }
};

// Prefers the stream the preview is already showing (no second capture, so Safari will not
// mute the preview); only opens its own capture when there is no preview stream or the
// preview stream looks broken, so we can tell an app problem from a device/OS problem
export const testCamera = async ({ deviceId, previewLocalVideoStream }) => {
  const result = { status: "fail", attempts: [] };

  let appStream = await getAppStream(previewLocalVideoStream);
  // Right after switching cameras the preview may still be on the old one - test the new one directly
  const appDeviceId = appStream?.getVideoTracks()[0]?.getSettings?.().deviceId;
  if (deviceId && appDeviceId && appDeviceId !== deviceId) appStream = null;
  if (appStream) {
    const track = appStream.getVideoTracks()[0];
    const frames = await analyzeVideoFrames(appStream);
    result.attempts.push({ source: "Preview stream used by the app", track: describeTrack(track), frames });
    if (frames.framesReceived && !frames.isBlack && !frames.isFrozen && track.readyState === "live") {
      result.status = "pass";
      result.snapshot = frames.snapshot;
      return result;
    }
  }

  let ownStream = null;
  try {
    const { stream, usedFallback } = await getMediaWithFallback("video", deviceId);
    ownStream = stream;
    const track = stream.getVideoTracks()[0];
    const frames = await analyzeVideoFrames(stream);
    result.attempts.push({
      source: usedFallback ? "Direct capture (selected camera unavailable - used default)" : "Direct capture of selected camera",
      track: describeTrack(track),
      frames,
    });
    result.snapshot = frames.snapshot;
    if (!frames.framesReceived) {
      result.status = "fail";
      result.problem = "The camera started but sent no picture. Another app may be holding the camera, or macOS is blocking it.";
    } else if (frames.isBlack) {
      result.status = "warn";
      result.problem = "The camera is sending a black picture. Check for a lens cover/privacy shutter, closed laptop lid with an external display, or another app using the camera.";
    } else if (frames.isFrozen) {
      result.status = "warn";
      result.problem = "The camera picture is frozen (frames are not changing). Close other apps using the camera and restart the browser.";
    } else {
      result.status = appStream ? "warn" : "pass";
      if (appStream) result.problem = "The camera works directly, but the LiveStream preview stream was not delivering a picture. Turn the camera off and on in the preview, or reload the page.";
      if (usedFallback) result.note = "The previously selected camera was not found; the default camera works. Pick it again in the Camera list.";
    }
  } catch (error) {
    result.attempts.push({ source: "Direct capture of selected camera", error: `${error.name}: ${error.message}` });
    result.status = "fail";
    result.problem = explainMediaError(error, "camera");
  } finally {
    ownStream?.getTracks().forEach((track) => track.stop());
  }
  return result;
};

// The caller creates the AudioContext synchronously inside the click handler - Safari only
// lets an AudioContext run when it is created during a user gesture
export const createAudioContext = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    return AudioContextClass ? new AudioContextClass() : null;
  } catch (e) {
    return null;
  }
};

export const testMicrophone = async ({ deviceId, audioContext, durationMs = 3000, onLevel }) => {
  const result = { status: "fail" };
  let stream = null;
  try {
    const capture = await getMediaWithFallback("audio", deviceId);
    stream = capture.stream;
    const track = stream.getAudioTracks()[0];
    result.track = describeTrack(track);
    if (capture.usedFallback) result.note = "The previously selected microphone was not found; the default microphone was tested.";

    if (!audioContext) {
      result.status = track.readyState === "live" ? "pass" : "fail";
      result.note = "Audio level could not be measured in this browser.";
      return result;
    }
    if (audioContext.state === "suspended") await audioContext.resume().catch(() => {});
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    const buffer = new Float32Array(analyser.fftSize);
    let peak = 0;
    let total = 0;
    let count = 0;
    const end = performance.now() + durationMs;
    while (performance.now() < end) {
      analyser.getFloatTimeDomainData(buffer);
      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) sumSquares += buffer[i] * buffer[i];
      const rms = Math.sqrt(sumSquares / buffer.length);
      peak = Math.max(peak, rms);
      total += rms;
      count++;
      onLevel?.(Math.min(1, rms * 8));
      await delay(50);
    }
    source.disconnect();
    onLevel?.(0);
    const toDb = (value) => (value > 0 ? Math.round(20 * Math.log10(value)) : -100);
    result.peakDb = toDb(peak);
    result.averageDb = toDb(total / (count || 1));
    result.audioContextState = audioContext.state;
    if (track.muted) {
      result.status = "fail";
      result.problem = "The microphone is muted by the system or hardware (check a mute key/switch or the input level in System Settings > Sound).";
    } else if (peak < 0.003) {
      result.status = "warn";
      result.problem = "No sound was picked up. Make sure you spoke during the test, the right microphone is selected, and the input volume is not at zero.";
    } else {
      result.status = "pass";
    }
  } catch (error) {
    result.status = "fail";
    result.error = `${error.name}: ${error.message}`;
    result.problem = explainMediaError(error, "microphone");
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
  }
  return result;
};

export const playTestTone = async ({ deviceId, audioContext }) => {
  const result = { played: false };
  if (!audioContext) {
    result.error = "Audio playback is not available in this browser.";
    return result;
  }
  try {
    if (audioContext.state === "suspended") await audioContext.resume();
    if (deviceId && typeof audioContext.setSinkId === "function") {
      await audioContext.setSinkId(deviceId);
      result.output = "Selected speaker";
    } else {
      result.output = deviceId ? "System default (this browser cannot pick a speaker)" : "System default";
    }
    const now = audioContext.currentTime;
    [523.25, 659.25, 783.99].forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const start = now + index * 0.35;
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.6);
      oscillator.connect(gain).connect(audioContext.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.65);
    });
    await delay(1400);
    result.played = true;
    result.audioContextState = audioContext.state;
  } catch (error) {
    result.error = `${error.name}: ${error.message}`;
  }
  return result;
};

// Known Mac setups that commonly explain "camera not working" reports
export const getDeviceHints = (devices) => {
  const hints = [];
  const cameras = devices.filter((d) => d.kind === "videoinput");
  if (cameras.some((d) => /iphone|continuity/i.test(d.label))) {
    hints.push("An iPhone (Continuity Camera) is listed as a camera. macOS may switch to it automatically - make sure the intended camera is selected.");
  }
  if (cameras.some((d) => /obs|virtual|snap camera|manycam|xsplit|mmhmm/i.test(d.label))) {
    hints.push("A virtual camera is installed. If it is selected but its app is closed, the video will be black - pick the built-in camera instead.");
  }
  return hints;
};

const formatSection = (title, entries) => {
  const lines = Object.entries(entries).map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : value}`);
  return `--- ${title} ---\n${lines.join("\n")}`;
};

const formatDevices = (devices, selected) => {
  if (!devices?.length) return "(none)";
  return devices
    .map((d) => {
      const isSelected = d.deviceId && Object.values(selected).includes(d.deviceId);
      return `${isSelected ? "*" : " "} ${d.kind}: ${d.label} [id ${shortId(d.deviceId)}]`;
    })
    .join("\n");
};

const STATUS_TEXT = { pass: "PASS", warn: "WARNING", fail: "FAIL", skipped: "NOT RUN" };

export const generateReportId = () =>
  `LS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

export const buildReport = ({ reportId, generatedAt, appState, system, acs, permissions, devicesBefore, devicesAfter, camera, microphone, speaker, findings, consoleLogs }) => {
  const selected = { camera: appState.selectedCameraId, microphone: appState.selectedMicrophoneId, speaker: appState.selectedSpeakerId };
  const cameraAttempts = (camera?.attempts || [])
    .map((attempt, index) => {
      const { snapshot, ...frames } = attempt.frames || {};
      return `Attempt ${index + 1}: ${attempt.source}\n` +
        (attempt.error ? `  Error: ${attempt.error}\n` : "") +
        (attempt.track ? `  Track: ${JSON.stringify(attempt.track)}\n` : "") +
        (attempt.frames ? `  Frames: ${JSON.stringify(frames)}\n` : "");
    })
    .join("");

  const { selectedCameraId, selectedMicrophoneId, selectedSpeakerId, ...appStateRest } = appState;

  return [
    "=== Vagaro LiveStream Troubleshooting Log ===",
    `Report ID: ${reportId}`,
    `Generated: ${generatedAt.toISOString()} (local: ${generatedAt.toString()})`,
    "",
    "--- Summary ---",
    `Camera: ${STATUS_TEXT[camera?.status || "skipped"]}${camera?.problem ? ` - ${camera.problem}` : ""}`,
    `Microphone: ${STATUS_TEXT[microphone?.status || "skipped"]}${microphone?.problem ? ` - ${microphone.problem}` : ""}`,
    `Speaker: ${STATUS_TEXT[speaker?.status || "skipped"]}${speaker?.problem ? ` - ${speaker.problem}` : ""}`,
    findings.length ? `Findings:\n${findings.map((f) => `  - ${f}`).join("\n")}` : "Findings: none",
    "",
    formatSection("System", system || {}),
    "",
    formatSection("Video service (ACS) compatibility", acs || {}),
    "",
    formatSection("Permissions", permissions || {}),
    "",
    formatSection("LiveStream app state", {
      ...appStateRest,
      "Selected camera id": shortId(selectedCameraId),
      "Selected microphone id": shortId(selectedMicrophoneId),
      "Selected speaker id": shortId(selectedSpeakerId),
    }),
    "",
    "--- Devices before test (* = selected) ---",
    formatDevices(devicesBefore, selected),
    "",
    "--- Devices after test (* = selected) ---",
    formatDevices(devicesAfter, selected),
    "",
    "--- Camera test ---",
    cameraAttempts || "(not run)",
    camera?.note ? `Note: ${camera.note}` : "",
    "--- Microphone test ---",
    microphone ? JSON.stringify({ ...microphone, problem: undefined }, null, 0) : "(not run)",
    "",
    "--- Speaker test ---",
    speaker ? JSON.stringify({ ...speaker, problem: undefined }, null, 0) : "(not run)",
    "",
    `--- Console log (last ${consoleLogs.length} entries) ---`,
    consoleLogs.length ? consoleLogs.join("\n") : "(empty)",
    "",
    "=== End of log ===",
  ].join("\n");
};

export const copyTextToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
  }
  // Clipboard API is blocked in some iframes and older Safari - fall back to execCommand
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch (e) {
    return false;
  }
};
